import {
  Fragment,
  useEffect,
  useRef,
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownUp, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useTable, type SortDirection } from './useTable';
import { EditCellForm } from './EditCellForm';
import { CellEditButton, CellEditPopover } from './CellEditPopover';
import { useSortableColumn } from './useSortableColumn';
import {
  getColumnDragAccessibility,
  useColumnDragReorder,
} from './useColumnDragReorder';

export type TableCell = {
  value: string | number | ReactNode;
  editValue?: string | number;
};

/** Spread last on the row, so a `data-testid` here wins over the one `_testIdSuffix` builds. */
export type RowAttributes = Record<`data-${string}`, string>;

export type TableRow = {
  id: TableCell;
  _sectionHeader?: ReactNode;
  // Detail rows are excluded from data-row numbering and the footer count, so
  // expanding a group never shifts other rows' testids or inflates the count.
  _detail?: boolean;
  // Renders as a single cell spanning every column, for detail content that is not column-shaped.
  // Implies _detail for numbering; _sectionHeader takes precedence when both are set.
  _fullWidth?: ReactNode;
  // Overrides the positional index in cell/edit testids; detail rows must set this,
  // since they have no positional index.
  _testIdSuffix?: string;
  // False suppresses the edit pencil on this row, regardless of column.isEditable.
  _editable?: boolean;
  _rowAttributes?: RowAttributes;
  // RowAttributes is here because `_rowAttributes` must be assignable to this index signature.
  [key: string]: TableCell | ReactNode | RowAttributes | undefined;
};

export type Justification = 'start' | 'end';

export interface TableColumn {
  header: string;
  key: string;
  isSortable?: boolean;
  width?: number; // Minimum column width in rem — column expands beyond this when space is available.
  maxWidth?: number; // Maximum column width in rem — column never grows beyond this regardless of available space.
  isEditable?: boolean;
  justification?: Justification;
  headerJustification?: Justification;
  editableType?: 'text' | 'number' | 'select';
  editableOptions?: { value: string; label: string }[];
  editableMin?: number;
  editableMax?: number;
  textShade?: 'light' | 'dark' | 'default';
  stickTo?: 'left' | 'right' | 'none';
  renderEditor?: (args: { row: TableRow; onDone: () => void }) => ReactNode;
  isEditorBusy?: (row: TableRow) => boolean;
}

export interface TableProps {
  rows: TableRow[];
  columns: TableColumn[];
  onSort?: (key: string, direction: SortDirection) => void;
  onEditSubmit?: (
    rowId: string,
    columnKey: string,
    newValue: string | number
  ) => void;
  className?: string;
  emptyMessage?: string;
  tableControls?: ReturnType<typeof useTable>;
  totalRowsCount?: number;
  name?: string;
  minWidth?: number;
  enableColumnReorder?: boolean;
  onColumnReorderEnd?: (newOrder: string[]) => void;
  striped?: boolean;
}

const HOVER_REVEAL_CLASS = 'opacity-0 group-hover:opacity-100';

type HeaderCellProps = ComponentProps<'th'> & {
  'data-stick-to'?: string;
  'data-column-key': string;
};

function SortableHeaderCell({
  column,
  style,
  children,
  ...thProps
}: HeaderCellProps & { column: TableColumn; children: ReactNode }) {
  const { t } = useTranslation('general');
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    style: sortableStyle,
  } = useSortableColumn(column.key);

  return (
    <th ref={setNodeRef} {...thProps} style={{ ...style, ...sortableStyle }}>
      <div className="flex items-center gap-1">
        <button
          ref={setActivatorNodeRef}
          type="button"
          {...attributes}
          {...listeners}
          onClick={(event) => event.stopPropagation()}
          className="shrink-0 text-neut-400 cursor-grab"
          aria-label={t('table.reorder_column')}
          data-testid={`column-grip-${column.key}`}
        >
          <GripVertical size={14} aria-hidden />
        </button>
        {children}
      </div>
    </th>
  );
}

export function Table({
  rows,
  columns,
  onSort,
  className = '',
  emptyMessage = 'No data available',
  onEditSubmit,
  tableControls,
  totalRowsCount,
  name,
  minWidth,
  enableColumnReorder = false,
  onColumnReorderEnd,
  striped = false,
}: TableProps) {
  const { t } = useTranslation('general');
  const internalControls = useTable({ onSort, onEditSubmit });
  const controls = tableControls || internalControls;

  const {
    sortKey,
    sortDirection,
    editing,
    isSubmittingEdit,
    handleSort,
    handleEditContinue,
    handleEditClick,
    handleEditDone,
    handleEditDoneFor,
  } = controls;

  const sortedColumns = [
    ...columns.filter((column) => column.stickTo === 'left'),
    ...columns.filter((column) => !column.stickTo || column.stickTo === 'none'),
    ...columns.filter((column) => column.stickTo === 'right'),
  ];
  const leftStickyColumns = sortedColumns.filter(
    (column) => column.stickTo === 'left'
  );
  const rightStickyColumns = sortedColumns.filter(
    (column) => column.stickTo === 'right'
  );
  const unpinnedColumnKeys = sortedColumns
    .filter((column) => !column.stickTo || column.stickTo === 'none')
    .map((column) => column.key);

  const { sensors, activeDragColumnKey, handleDragStart, handleDragEnd } =
    useColumnDragReorder(unpinnedColumnKeys, (newOrder) =>
      onColumnReorderEnd?.(newOrder)
    );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const leftShadowRef = useRef<HTMLDivElement>(null);
  const rightShadowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    function update() {
      if (!container) return;
      const scrollLeft = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const canRight = Math.ceil(scrollLeft) < maxScroll;

      const leftHeaders = Array.from(
        container.querySelectorAll<HTMLElement>('th[data-stick-to="left"]')
      );
      const rightHeaders = Array.from(
        container.querySelectorAll<HTMLElement>('th[data-stick-to="right"]')
      );
      const leftWidthPx = leftHeaders.reduce(
        (sum, th) => sum + th.offsetWidth,
        0
      );
      const rightWidthPx = rightHeaders.reduce(
        (sum, th) => sum + th.offsetWidth,
        0
      );

      if (leftShadowRef.current) {
        leftShadowRef.current.style.left = `${scrollLeft + leftWidthPx}px`;
        leftShadowRef.current.style.opacity = canRight ? '1' : '0';
        leftShadowRef.current.classList.toggle('sticky-col-glow', canRight);
      }
      if (rightShadowRef.current) {
        const rightShadowWidth = rightShadowRef.current.offsetWidth;
        rightShadowRef.current.style.left = `${scrollLeft + container.clientWidth - rightWidthPx - rightShadowWidth}px`;
        rightShadowRef.current.style.opacity = canRight ? '1' : '0';
        rightShadowRef.current.classList.toggle('sticky-col-glow', canRight);
      }
    }

    update();
    container.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      container.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [rows, columns]);

  const getSortIcon = (columnKey: string) => {
    if (sortKey !== columnKey) {
      return <ArrowDownUp size={14} className="text-neut-400" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp size={14} className="ml-1 text-neut-600" />;
    } else if (sortDirection === 'desc') {
      return <ArrowDown size={14} className="ml-1 text-neut-600" />;
    }

    return null;
  };

  if (rows.length === 0) {
    return (
      <div className={`border border-gray-200 rounded-lg ${className}`}>
        <div className="p-8 text-center text-gray-500">{emptyMessage}</div>
      </div>
    );
  }
  const columnShadeClass = {
    light: 'text-neut-500',
    dark: 'text-neut-900 font-medium',
    default: 'text-neut-700',
  };

  function renderHeaderCell(column: TableColumn) {
    const isReorderable =
      enableColumnReorder && (!column.stickTo || column.stickTo === 'none');
    const headerCellProps: HeaderCellProps = {
      'data-stick-to': column.stickTo,
      'data-column-key': column.key,
      className: `relative px-4 py-3 text-left text-xs text-neut-600 capitalize font-medium bg-neut-50 ${column.isSortable ? 'cursor-pointer hover:bg-neut-100' : ''}`,
      style: {
        minWidth: column.width ? column.width + 'rem' : undefined,
        maxWidth: column.maxWidth ? column.maxWidth + 'rem' : undefined,
        ...getColumnStickyStyle(column),
      },
      onClick: () => column.isSortable && handleSort(column.key),
    };
    const headerContent = (
      <div
        className={`flex items-center ${getJustificationClass(column.headerJustification ?? column.justification)} gap-1`}
      >
        {column.header}
        {column.isSortable && getSortIcon(column.key)}
      </div>
    );
    return isReorderable ? (
      <SortableHeaderCell key={column.key} column={column} {...headerCellProps}>
        {headerContent}
      </SortableHeaderCell>
    ) : (
      <th key={column.key} {...headerCellProps}>
        {headerContent}
      </th>
    );
  }

  function renderEditAffordance(
    column: TableColumn,
    row: TableRow,
    {
      isEditingThisCell,
      rowTestIdSuffix,
    }: { isEditingThisCell: boolean; rowTestIdSuffix: string }
  ): ReactNode {
    const rowId = String(row.id.value);
    const pencilProps = {
      onOpen: () => handleEditClick(column.key, rowId),
      columnLabel: column.header,
      testId: `edit-${column.key}-${rowTestIdSuffix}`,
    };
    const { renderEditor } = column;

    if (!renderEditor) {
      return <CellEditButton {...pencilProps} className={HOVER_REVEAL_CLASS} />;
    }

    const onDoneForThisCell = handleEditDoneFor(column.key, rowId);

    return (
      <CellEditPopover
        {...pencilProps}
        isOpen={isEditingThisCell}
        onDone={onDoneForThisCell}
        isBusy={column.isEditorBusy ? column.isEditorBusy(row) : false}
        anchorClassName={HOVER_REVEAL_CLASS}
        renderEditor={() => renderEditor({ row, onDone: onDoneForThisCell })}
      />
    );
  }

  function getColumnStickyStyle(column: TableColumn): CSSProperties {
    if (!column.stickTo || column.stickTo === 'none') return {};

    if (column.stickTo === 'left') {
      const columnIndex = leftStickyColumns.findIndex(
        (leftColumn) => leftColumn.key === column.key
      );
      const leftOffset = leftStickyColumns
        .slice(0, columnIndex)
        .reduce((sum, leftColumn) => sum + (leftColumn.width ?? 0), 0);
      return { position: 'sticky', left: `${leftOffset}rem`, zIndex: 1 };
    }

    const columnIndex = rightStickyColumns.findIndex(
      (rightColumn) => rightColumn.key === column.key
    );
    const rightOffset = rightStickyColumns
      .slice(columnIndex + 1)
      .reduce((sum, rightColumn) => sum + (rightColumn.width ?? 0), 0);
    return { position: 'sticky', right: `${rightOffset}rem`, zIndex: 1 };
  }

  function getJustificationClass(
    justification: Justification | undefined
  ): string {
    return justification === 'end' ? 'justify-end' : 'justify-start';
  }

  // Index rows by data position (section headers excluded) so testids like
  // cell-product-3 / edit-stock-3 always refer to the 4th data row, no matter
  // how many section headers precede it. Callers that stamp their own per-row
  // testids from the data array (e.g. edit-inv-3) then stay aligned.
  const dataRowIndexByRow = new Map<TableRow, number>();
  const shadedRows = new Set<TableRow>();
  let dataRowIndex = 0;
  let inShadedBand = false;
  for (const row of rows) {
    if (row._sectionHeader) continue;
    if (!row._detail && row._fullWidth === undefined) {
      dataRowIndexByRow.set(row, dataRowIndex);
      inShadedBand = striped && dataRowIndex % 2 === 1;
      dataRowIndex++;
    }
    if (inShadedBand) shadedRows.add(row);
  }
  const rowHoverGroup = striped ? 'group/row' : '';
  const cellHover = striped ? 'group-hover/row:bg-neut-200' : '';

  const tableContent = (
    <>
      <div
        ref={scrollContainerRef}
        className={`overflow-x-auto relative ${className}`}
      >
        {leftStickyColumns.length > 0 && (
          <div
            ref={leftShadowRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '16px',
              background:
                'linear-gradient(to right, rgba(0,0,0,0.12), transparent)',
              pointerEvents: 'none',
              zIndex: 30,
              opacity: 0,
            }}
          />
        )}
        {rightStickyColumns.length > 0 && (
          <div
            ref={rightShadowRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '28px',
              background:
                'linear-gradient(to left, rgba(0,0,0,0.12), transparent)',
              pointerEvents: 'none',
              zIndex: 30,
              opacity: 0,
            }}
          />
        )}
        <table style={{ minWidth: (minWidth || 0) + 'rem' }} className="w-full">
          <thead className="bg-neut-50 border-b border-neut-200">
            <tr>
              {enableColumnReorder ? (
                <SortableContext
                  items={unpinnedColumnKeys}
                  strategy={horizontalListSortingStrategy}
                >
                  {sortedColumns.map((column) => renderHeaderCell(column))}
                </SortableContext>
              ) : (
                sortedColumns.map((column) => renderHeaderCell(column))
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row, rowIndex) => {
              // Tailwind's divide-y draws the rule as each row's bottom border, so a
              // detail row's rule to the row it expands lives on that preceding row.
              const precedesDetailRow =
                rows[rowIndex + 1]?._fullWidth !== undefined;
              const detailRuleClass = precedesDetailRow
                ? 'border-b-neut-100'
                : '';
              const cellBackground = shadedRows.has(row)
                ? 'bg-neut-bg'
                : 'bg-neut-bg-alt';
              if (row._sectionHeader) {
                return (
                  <tr
                    key={String(row.id.value)}
                    className="bg-neut-50 border-t border-b border-neut-200"
                    {...row._rowAttributes}
                  >
                    <td
                      colSpan={sortedColumns.length}
                      className="px-6 py-3 text-sm font-semibold text-neut-prim"
                    >
                      {row._sectionHeader}
                    </td>
                  </tr>
                );
              }
              const dataRowIndex = dataRowIndexByRow.get(row);
              const rowTestIdSuffix =
                row._testIdSuffix ??
                (dataRowIndex !== undefined
                  ? String(dataRowIndex)
                  : String(row.id.value));
              if (row._fullWidth !== undefined) {
                return (
                  <tr
                    key={String(row.id.value)}
                    className={detailRuleClass}
                    {...row._rowAttributes}
                  >
                    <td
                      colSpan={sortedColumns.length}
                      className={`px-4 py-3 text-sm whitespace-normal break-words text-neut-700 ${cellBackground}`}
                      data-testid={`cell-full-width-${rowTestIdSuffix}`}
                    >
                      {row._fullWidth}
                    </td>
                  </tr>
                );
              }
              return (
                <Fragment key={String(row.id.value)}>
                  <tr
                    className={`${rowHoverGroup} ${detailRuleClass}`}
                    {...row._rowAttributes}
                  >
                    {sortedColumns.map((column, idx) => {
                      const isEditingThisCell =
                        editing.isEditing &&
                        editing.columnKey === column.key &&
                        editing.rowId === String(row.id.value);
                      return (
                        <td
                          key={column.key}
                          className={`relative px-4 py-3 text-sm whitespace-normal group ${cellBackground} ${cellHover} ${columnShadeClass[column.textShade ?? 'default']}`}
                          style={getColumnStickyStyle(column)}
                        >
                          <div
                            className={`flex gap-3 items-center break-words ${getJustificationClass(column.justification)}`}
                            data-testid={`cell-${column.key}-${rowTestIdSuffix}`}
                          >
                            {(row[column.key] as TableCell | undefined)?.value}
                            {column.isEditable && row._editable !== false
                              ? renderEditAffordance(column, row, {
                                  isEditingThisCell,
                                  rowTestIdSuffix,
                                })
                              : null}
                          </div>
                          {isEditingThisCell && !column.renderEditor ? (
                            <EditCellForm
                              defaultValue={
                                ((row as Record<string, TableCell>)[column.key]
                                  .editValue ??
                                  (row as Record<string, TableCell>)[column.key]
                                    .value) as string | number
                              }
                              editableType={column.editableType}
                              editableOptions={column.editableOptions}
                              min={column.editableMin}
                              max={column.editableMax}
                              isSubmitting={isSubmittingEdit}
                              onSubmit={handleEditContinue}
                              onCancel={handleEditDone}
                              testIds={{
                                input: `input-${column.key}-${rowTestIdSuffix}`,
                                submit: `check-button-${column.key}-${rowTestIdSuffix}`,
                                cancel: `cancel-button-${rowTestIdSuffix}-${idx}`,
                              }}
                            />
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            {totalRowsCount !== undefined ? (
              <tr>
                <td
                  colSpan={sortedColumns.length}
                  className="px-6 py-3 border-t border-neut-border bg-neut-50"
                >
                  <p className="text-xs text-neut-sec">
                    Showing{' '}
                    <span className="font-semibold">
                      {dataRowIndexByRow.size}
                    </span>{' '}
                    of <span className="font-semibold">{totalRowsCount}</span>{' '}
                    {name}
                  </p>
                </td>
              </tr>
            ) : null}
          </tfoot>
        </table>
      </div>
    </>
  );

  const activeDragColumn = activeDragColumnKey
    ? sortedColumns.find((column) => column.key === activeDragColumnKey)
    : undefined;

  return enableColumnReorder ? (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      accessibility={getColumnDragAccessibility(
        t,
        (columnKey) =>
          sortedColumns.find((column) => column.key === columnKey)?.header ??
          columnKey
      )}
    >
      {tableContent}
      <DragOverlay dropAnimation={null}>
        {activeDragColumn ? (
          <div
            data-testid="column-drag-overlay"
            className="flex items-center gap-1 px-4 py-3 text-xs text-neut-600 capitalize font-medium bg-white rounded-md shadow-lg border border-neut-200"
          >
            <GripVertical size={14} className="text-neut-400" aria-hidden />
            {activeDragColumn.header}
            {activeDragColumn.isSortable && getSortIcon(activeDragColumn.key)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  ) : (
    tableContent
  );
}
